import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import authService, { resolveStytchBaseUrl } from 'services/auth/authService';
import useOrganization from 'hooks/useOrganization';
import { validateEmail as validateEmailUtil } from 'utils/validators.js';
import UnifiedLoginCarousel from './UnifiedLoginCarousel';

import styles from './MiraUnifiedLogin.module.css';

const USER_TYPES = {
  buyer: 'buyer',
  specialist: 'specialist',
};

const getOrgId = (org) => org?.stytch_organization_id || org?.organization_id || org?.id || '';

// Defaults to `specialist`, not `buyer`. Every route passes it explicitly, but
// the buyer branch short-circuits the auth path, so a caller that forgot the
// prop would render a form that silently cannot sign anyone in.
const MiraUnifiedLogin = ({ initialUserType = USER_TYPES.specialist }) => {
  const location = useLocation();
  const {
    organization: detectedOrg,
    subdomain: detectedSubdomain,
    loading: orgLoading,
    error: orgError,
    errorStatus: orgErrorStatus,
    isResolved: orgResolved,
  } = useOrganization();

  // Kept as state rather than ripped out: `isSpecialist` still gates a dozen
  // branches through this file, and collapsing them all is a larger change than
  // this one wants to be. Nothing sets it to `buyer` any more, so those branches
  // simply always take the specialist path.
  const [userType] = useState(
    initialUserType === USER_TYPES.buyer ? USER_TYPES.buyer : USER_TYPES.specialist
  );

  const [email, setEmail] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [discoveredOrganizations, setDiscoveredOrganizations] = useState([]);
  const [isDiscovering, setIsDiscovering] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [isMembershipChecking, setIsMembershipChecking] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipMessage, setMembershipMessage] = useState('');
  const [membershipConfirmed, setMembershipConfirmed] = useState(false);
  const [redirectMessageKey, setRedirectMessageKey] = useState(0);

  const isSubdomainOrg = useRef(false);
  const redirectTimersRef = useRef([]);
  const discoveryTimeoutRef = useRef(null);
  const membershipTimeoutRef = useRef(null);
  const skipResolveRef = useRef(false);
  const formRef = useRef(null);
  const submitInFlightRef = useRef(false);

  const clearTransientMessages = useCallback(() => {
    setError('');
    setWarningMessage('');
    setStatusMessage('');
    setShowContactAdmin(false);
    setEmailError('');
  }, []);

  const clearRedirectFlow = useCallback(() => {
    redirectTimersRef.current.forEach((timer) => clearTimeout(timer));
    redirectTimersRef.current = [];
  }, []);

  const clearInputTimers = useCallback(() => {
    if (discoveryTimeoutRef.current) {
      clearTimeout(discoveryTimeoutRef.current);
      discoveryTimeoutRef.current = null;
    }
    if (membershipTimeoutRef.current) {
      clearTimeout(membershipTimeoutRef.current);
      membershipTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearRedirectFlow();
      clearInputTimers();
    };
  }, [clearRedirectFlow, clearInputTimers]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const autoEmail = params.get('email');
    const autoSubmit = params.get('autosubmit');
    const resolvedOrgId = getOrgId(detectedOrg) || organizationId;
    if (autoEmail && autoSubmit === 'true') {
      setEmail(autoEmail);
      skipResolveRef.current = Boolean(resolvedOrgId);
      const timer = setTimeout(() => {
        formRef.current?.requestSubmit();
      }, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.search, detectedOrg, organizationId]);

  const validateEmail = useCallback((candidateEmail) => {
    const isValid = validateEmailUtil(candidateEmail);
    if (!isValid) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  }, []);

  const checkMembershipForEmail = useCallback(
    async (candidateEmail, orgId) => {
      clearRedirectFlow();

      if (!candidateEmail || !orgId || !validateEmail(candidateEmail)) {
        return;
      }

      setIsMembershipChecking(true);
      setMembershipStatus(null);
      setMembershipMessage('');
      setMembershipConfirmed(false);

      try {
        const response = await authService.checkMembership(candidateEmail, orgId);

        if (response.redirect_url) {
          const destination = response.home_branch_slug
            ? `${response.home_branch_slug}.${response.home_organization_slug}`
            : response.home_organization_slug;

          setMembershipStatus('redirecting');
          setMembershipMessage('You are not a member of this organization or branch.');
          setWarningMessage('');
          setShowContactAdmin(false);
          setMembershipConfirmed(false);
          setRedirectMessageKey((seed) => seed + 1);

          redirectTimersRef.current.push(
            setTimeout(() => {
              setMembershipMessage(
                destination
                  ? `Redirecting you to ${destination}...`
                  : 'Redirecting you to your assigned organization or branch...'
              );
              setRedirectMessageKey((seed) => seed + 1);
            }, 2000)
          );

          redirectTimersRef.current.push(
            setTimeout(() => {
              window.location.replace(response.redirect_url);
            }, 4200)
          );
          return;
        }

        if (response.error) {
          setMembershipStatus('error');
          setMembershipMessage('');
          setMembershipConfirmed(false);
          return;
        }

        if (response.is_member) {
          setMembershipStatus('member');
          setMembershipMessage('');
          setWarningMessage('');
          setShowContactAdmin(false);
          setMembershipConfirmed(true);
          return;
        }

        const orgName = detectedOrg?.name || response.home_organization_name || 'this organization';
        const message = `You are not a member of ${orgName}. Please contact your administrator to request access.`;
        setMembershipStatus('not-member');
        setMembershipMessage(message);
        setWarningMessage(message);
        setShowContactAdmin(true);
        setMembershipConfirmed(false);
      } catch (membershipError) {
        setMembershipStatus('error');
        setMembershipMessage('');
        setMembershipConfirmed(false);
      } finally {
        setIsMembershipChecking(false);
      }
    },
    [clearRedirectFlow, detectedOrg, validateEmail]
  );

  const discoverOrganizations = useCallback(
    async (candidateEmail) => {
      if (!validateEmail(candidateEmail)) {
        return;
      }

      if (isSubdomainOrg.current && organizationId) {
        setIsDiscovering(false);
        return;
      }

      setIsDiscovering(true);

      try {
        const response = await authService.discoverOrganizations(candidateEmail);
        const organizations = response?.organizations || [];
        setDiscoveredOrganizations(organizations);

        if (organizations.length > 0 && !organizationId) {
          const candidateOrgId = getOrgId(organizations[0]);
          if (candidateOrgId) {
            setOrganizationId(candidateOrgId);
          }
        }
      } catch (discoveryError) {
        if (discoveryError?.response?.status === 422 && detectedOrg) {
          const fallbackOrgId = getOrgId(detectedOrg);
          if (fallbackOrgId) {
            setOrganizationId(fallbackOrgId);
            setDiscoveredOrganizations([
              {
                id: fallbackOrgId,
                name: detectedOrg.name,
                slug: detectedOrg.slug || detectedSubdomain,
                isPlatform: detectedOrg.is_platform,
              },
            ]);
          }
        }
      } finally {
        setIsDiscovering(false);
      }
    },
    [detectedOrg, detectedSubdomain, organizationId, validateEmail]
  );

  const resetSpecialistState = useCallback(() => {
    setOrganizationId('');
    setDiscoveredOrganizations([]);
    setIsDiscovering(false);
    setIsMembershipChecking(false);
    setMembershipStatus(null);
    setMembershipMessage('');
    setMembershipConfirmed(false);
    clearRedirectFlow();
    clearInputTimers();
  }, [clearInputTimers, clearRedirectFlow]);

  const handleEmailChange = useCallback(
    (nextEmail) => {
      setEmail(nextEmail);
      clearTransientMessages();
      setMembershipStatus(null);
      setMembershipMessage('');
      setMembershipConfirmed(false);
      clearRedirectFlow();
      clearInputTimers();

      if (userType !== USER_TYPES.specialist) {
        return;
      }

      const isSubdomainFlow = isSubdomainOrg.current && organizationId && detectedOrg;

      if (!nextEmail.includes('@') || !nextEmail.split('@')[1]) {
        if (!isSubdomainFlow) {
          setDiscoveredOrganizations([]);
          setOrganizationId('');
        }
        return;
      }

      if (isSubdomainFlow) {
        membershipTimeoutRef.current = setTimeout(() => {
          checkMembershipForEmail(nextEmail, organizationId);
        }, 500);
        return;
      }

      discoveryTimeoutRef.current = setTimeout(() => {
        if (isSubdomainOrg.current && organizationId) {
          return;
        }
        discoverOrganizations(nextEmail);
      }, 800);
    },
    [
      checkMembershipForEmail,
      clearInputTimers,
      clearRedirectFlow,
      clearTransientMessages,
      detectedOrg,
      discoverOrganizations,
      organizationId,
      userType,
    ]
  );

  useEffect(() => {
    if (userType !== USER_TYPES.specialist) {
      return;
    }

    if (detectedOrg && !organizationId) {
      const orgId = getOrgId(detectedOrg);
      if (orgId) {
        setOrganizationId(orgId);
        isSubdomainOrg.current = true;
      }
    }
  }, [detectedOrg, organizationId, userType]);

  useEffect(() => {
    if (userType !== USER_TYPES.specialist) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const callbackError = urlParams.get('error');
    const orgSlug = urlParams.get('org') || urlParams.get('queryorg');

    if (callbackError) {
      setError(decodeURIComponent(callbackError));
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, document.title, `${newUrl.pathname}${newUrl.search}`);
    }

    if (detectedOrg && orgResolved && !organizationId) {
      const orgId = getOrgId(detectedOrg);
      if (orgId) {
        setOrganizationId(orgId);
        isSubdomainOrg.current = true;
        setDiscoveredOrganizations([]);
      }
    }

    if (orgSlug && orgResolved && !detectedOrg) {
      return;
    }
  }, [detectedOrg, orgResolved, organizationId, userType]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    clearTransientMessages();

    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      submitInFlightRef.current = false;
      return;
    }

    const isSpecialist = userType === USER_TYPES.specialist;

    if (isSpecialist && membershipStatus === 'not-member') {
      setError('You are not authorized for this organization.');
      submitInFlightRef.current = false;
      return;
    }

    setSubmitting(true);

    try {
      if (!skipResolveRef.current) {
        const resolved = await authService.resolveLogin(trimmedEmail);
        const resolvedType = resolved?.user_type;
        const redirectUrl = resolved?.redirect_url;
        const isBuyer = !isSpecialist;
        const isTypeMismatch =
          (isBuyer && resolvedType === 'employee') ||
          (isSpecialist && resolvedType === 'consumer');

        if (redirectUrl) {
          let needsRedirect = isTypeMismatch;
          try {
            const target = new URL(redirectUrl);
            if (target.host !== window.location.host) {
              needsRedirect = true;
            }
          } catch (_err) {
            // ignore parsing errors
          }
          if (needsRedirect) {
            const target = new URL(redirectUrl);
            target.searchParams.set('email', trimmedEmail);
            target.searchParams.set('autosubmit', 'true');
            setStatusMessage('Redirecting to your assigned portal...');
            window.location.replace(target.toString());
            return;
          }
        }
      } else {
        skipResolveRef.current = false;
      }

      const effectiveOrgId = organizationId || getOrgId(detectedOrg);

      if (isSpecialist && !effectiveOrgId) {
        setError('Please select an organization to continue.');
        return;
      }

      if (!isSpecialist) {
        const response = await authService.sendConsumerMagicLink(trimmedEmail);
        if (response?.success === false) {
          setError(response.message || 'Failed to send magic link. Please try again.');
          return;
        }
        sessionStorage.setItem('mira_awaiting_magic_link', 'true');
        setError('');
        setWarningMessage('');
        setStatusMessage('Magic link sent. Please check your email to continue.');
        return;
      }

      const canonicalBase = resolveStytchBaseUrl();
      const redirectUrl = `${canonicalBase}/auth/callback`;

      const response = await authService.sendB2BMagicLink(
        trimmedEmail,
        effectiveOrgId,
        redirectUrl,
        redirectUrl
      );

      if (response?.success === true) {
        sessionStorage.setItem('mira_awaiting_magic_link', 'true');
        setError('');
        setWarningMessage('');
        setStatusMessage(response.message || 'Magic link sent. Please check your email to continue.');
        return;
      }

      if (response?.success === false && response.display_type === 'warning') {
        setWarningMessage(response.message || 'Unable to verify organization membership.');
        if (response.next_step === 'contact_admin') {
          setShowContactAdmin(true);
        }
        return;
      }

      if (response?.success === false) {
        setError(response.message || 'Failed to send magic link. Please try again.');
        return;
      }

      setError('');
      setWarningMessage('');
      setStatusMessage('Magic link sent. Please check your email to continue.');
    } catch (submitError) {
      let message = 'Unable to send magic link. Please try again or contact support.';

      if (submitError?.response?.status === 404) {
        message = 'Organization not found. Please contact your administrator.';
      } else if (submitError?.response?.status === 403) {
        message = 'You are not authorized to access this organization.';
      } else if (submitError?.response?.status === 500) {
        message = 'Server error. Please wait a moment and try again.';
      } else if (submitError?.response?.status === 422) {
        message = 'Invalid request. Please verify your details and try again.';
      } else if (submitError?.response?.data?.detail) {
        message = submitError.response.data.detail;
      } else if (submitError?.message) {
        message = submitError.message;
      }

      setError(message);
    } finally {
      setSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  const isSpecialist = userType === USER_TYPES.specialist;
  const hasOrgResolutionError = isSpecialist && Boolean(orgError) && orgResolved && Boolean(detectedSubdomain);

  const submitDisabled =
    submitting ||
    !email.trim() ||
    (isSpecialist && (!organizationId || membershipStatus === 'not-member' || isMembershipChecking)) ||
    hasOrgResolutionError;

  return (
    <div className={styles.page}>
      <div className={styles.leftPanel}>
        <div className={styles.formCard}>
          <div className={styles.logoRow}>
            {/* No text label beside it: the wordmark is the name. */}
            <img src="/opus-wordmark-dark.png" alt="Opus Capital Markets Consultants" className={styles.logo} />
          </div>

          <h1 className={styles.title}>Sign In.</h1>
          {/* One audience, so no toggle and no conditional copy. The Home Buyer
              half signed in a borrower who no longer has an account. */}
          <p className={styles.subtitle}>
            Access specialist tools and manage your mortgage pipeline with confidence.
          </p>

          <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label htmlFor="email" className={styles.emailLabel}>
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(event) => handleEmailChange(event.target.value)}
                onBlur={() => email && validateEmail(email)}
                className={styles.emailInput}
                placeholder="your.email@company.com"
                autoComplete="email"
                required
              />
              {emailError && <p className={styles.emailError}>{emailError}</p>}
              {isSpecialist && isDiscovering && (
                <p className={styles.helperText}>Discovering organizations for this email...</p>
              )}
              {isSpecialist && orgLoading && !orgResolved && (
                <p className={styles.helperText}>Detecting organization context...</p>
              )}
              {isSpecialist && isMembershipChecking && (
                <p className={styles.helperText}>Verifying organization membership...</p>
              )}
              {isSpecialist && membershipConfirmed && membershipStatus === 'member' && (
                <p className={styles.helperText}>Membership verified.</p>
              )}
            </div>

            {isSpecialist && discoveredOrganizations.length > 1 && !detectedOrg && (
              <div className={styles.orgBlock}>
                <label htmlFor="organization" className={styles.orgLabel}>
                  Organization
                </label>
                <select
                  id="organization"
                  value={organizationId}
                  onChange={(event) => setOrganizationId(event.target.value)}
                  className={styles.orgSelect}
                  disabled={isDiscovering || submitting}
                >
                  <option value="">Select your organization...</option>
                  {discoveredOrganizations.map((organization) => {
                    const optionOrgId = getOrgId(organization);
                    return (
                      <option key={optionOrgId} value={optionOrgId}>
                        {organization.name || organization.slug || optionOrgId}
                      </option>
                    );
                  })}
                </select>
                <p className={styles.orgHelper}>
                  Multiple organizations found for your email domain. Please select one.
                </p>
              </div>
            )}

            {hasOrgResolutionError && (
              <p className={styles.errorMessage}>
                {orgErrorStatus === 429
                  ? 'Too many requests from your network. Please wait a minute and reload the page.'
                  : 'We could not find an organization for this subdomain. Please verify the URL or contact your administrator.'}
              </p>
            )}

            {membershipStatus === 'redirecting' && membershipMessage && (
              <p className={styles.warningMessage} key={`redirect-message-${redirectMessageKey}`}>
                {membershipMessage}
              </p>
            )}

            {membershipStatus === 'not-member' && membershipMessage && (
              <div className={styles.warningMessage}>
                <p>{membershipMessage}</p>
                <div className={styles.contactAdminHelp}>
                  <p>To gain access:</p>
                  <ul>
                    <li>Contact your organization administrator.</li>
                    <li>Request to be added as a member.</li>
                    <li>Or email support at support@mira.com.</li>
                  </ul>
                </div>
              </div>
            )}

            {warningMessage && membershipStatus !== 'not-member' && (
              <div className={styles.warningMessage}>
                <p>{warningMessage}</p>
                {showContactAdmin && (
                  <div className={styles.contactAdminHelp}>
                    <p>To gain access:</p>
                    <ul>
                      <li>Contact your organization administrator.</li>
                      <li>Request to be added as a member.</li>
                      <li>Or email support at support@mira.com.</li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            {error && <p className={styles.errorMessage}>{error}</p>}
            {statusMessage && <p className={styles.statusMessage}>{statusMessage}</p>}

            <button type="submit" disabled={submitDisabled} className={styles.submitButton}>
              <span className={styles.submitButtonText}>
                {submitting ? 'Sending magic link...' : 'Send Magic Link'}
              </span>
              <ArrowRight className={styles.submitButtonIcon} strokeWidth={2.5} />
            </button>
          </form>

          <p className={styles.legal}>
            Protected by reCAPTCHA and subject to the
            <a href="/privacy-policy" className={styles.legalLink}>
              Privacy Policy
            </a>
            and
            <a href="/terms-of-service" className={styles.legalLink}>
              Terms of Service
            </a>
            .
          </p>
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.rightPanelOverlay} />
        <div className={styles.rightPanelInner}>
          <UnifiedLoginCarousel userType={userType} />
        </div>
      </div>
    </div>
  );
};

export default MiraUnifiedLogin;
